import { articleDescriptionMaxLength } from '@texas/resources/constants';
import { useForm } from 'react-hook-form';
import { reactEvents } from '@bridge/reactEvents';
import { useActiveSession } from '@texas/hooks/useSession';
import { useToast } from '@chakra-ui/react';
import {
  ArticleCopyTarget,
  ArticleCopyVariantOptions,
  articlesApi,
} from '@texas/api/endpoints/articlesApi';
import {
  CustomerConcept,
  customersApi,
} from '@texas/api/endpoints/customersApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { customerEvents } from '@texas/components/customer/events';
import { useActiveContext } from '@texas/hooks/useActiveContext';
import { ReactSelectOption } from '@texas/types';
import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  buildArticleNumbers,
  getArticleNumberLimits,
} from '../numbering/articleNumbers';
import { useArticleAvailability } from '../numbering/useArticleAvailability';
import { useArticleClashes } from '../numbering/useArticleClashes';
import { getCopyWarnings } from './warnings';
import { getActiveWarnings } from '../warnings';
import { WarningItem } from '@texas/components/shared/WarningSummary';
import { useCustomerSearch } from './useCustomerSearch';

export interface CopyArticleProviderProps {
  sourceArticleId?: number;
  customerId?: number;
  conceptId?: number;
}

export interface VariantSelection extends ArticleCopyVariantOptions {
  included: boolean;
}

interface CopyArticleForm {
  sourceCustomerId: number;
  sourceArticleId: number;
  sourceCustomerOption: ReactSelectOption | null;
  sourceArticleOption: ReactSelectOption | null;
  concepts: CustomerConcept[];
  sourceArticles: { id: number; itemCode: string; name: string }[];
  destinationCustomerId: number;
  destinationCustomerOption: ReactSelectOption | null;
  destinationConceptId: number | null;
  copyMode: CopyMode;
  itemCode: string;
  name: string;
  position: NumberPosition;
  rangeStart: number;
  rangeEnd: number;
  padding: number;
  contact: boolean;
  tags: boolean;
  targetTiers: boolean;
  branchIds: number[];
  ignoreClashingItemCodes: boolean;
  variants: VariantSelection[];
}

export type CopyMode = 'single' | 'range';
export type NumberPosition = 'before' | 'after';

function useCopyArticleState({
  sourceArticleId: initialSourceArticleId,
  customerId: initialCustomerId,
  conceptId: initialConceptId,
}: CopyArticleProviderProps) {
  const warningScope = useId();
  const { t } = useTranslation();
  const toast = useToast();
  const { currentUser } = useActiveSession();
  const {
    data: source,
    loading,
    error,
    refetch,
  } = useApiResource(articlesApi.getCopySource);
  const {
    request: copy,
    loading: copying,
    error: copyError,
  } = useApiRequest(articlesApi.copyArticle);
  const { watch, setValue, getValues } = useForm<CopyArticleForm>({
    defaultValues: {
      sourceCustomerId: 0,
      sourceArticleId: initialSourceArticleId ?? 0,
      sourceCustomerOption: null,
      sourceArticleOption: null,
      concepts: [],
      sourceArticles: [],
      destinationCustomerId: initialCustomerId ?? 0,
      destinationCustomerOption: null,
      destinationConceptId: initialConceptId ?? null,
      copyMode: 'single',
      itemCode: '',
      name: '',
      position: 'after',
      rangeStart: 1,
      rangeEnd: 3,
      padding: 2,
      contact: true,
      tags: true,
      targetTiers: true,
      branchIds: [],
      ignoreClashingItemCodes: false,
      variants: [],
    },
  });
  const {
    sourceCustomerId,
    sourceArticleId,
    sourceCustomerOption,
    sourceArticleOption,
    concepts,
    sourceArticles,
    destinationCustomerId,
    destinationCustomerOption,
    destinationConceptId,
    copyMode,
    itemCode,
    name,
    position,
    rangeStart,
    rangeEnd,
    padding,
    contact,
    tags,
    targetTiers,
    branchIds,
    ignoreClashingItemCodes,
    variants,
  } = watch();

  const sourceCustomerSearch = useCustomerSearch();
  const destinationCustomerSearch = useCustomerSearch();
  const refreshSourceCustomers = sourceCustomerSearch.refresh;
  const refreshDestinationCustomers = destinationCustomerSearch.refresh;
  const destinationWasSelected = useRef(
    Boolean(initialCustomerId) || Boolean(initialConceptId),
  );

  const loadSourceCustomer = useCallback(
    (nextCustomerId: number) => {
      setValue('sourceCustomerId', nextCustomerId);
      setValue('sourceArticleId', 0);
      setValue('sourceArticleOption', null);
      setValue('sourceArticles', []);
      articlesApi
        .getDirectoryArticles(null, nextCustomerId)
        .then((response) => {
          if (!response.hasError) setValue('sourceArticles', response.data);
        });
    },
    [setValue],
  );

  useEffect(
    () =>
      customerEvents.brandCreated.sub(() => {
        refreshSourceCustomers();
        refreshDestinationCustomers();
      }),
    [refreshSourceCustomers, refreshDestinationCustomers],
  );

  useEffect(() => {
    if (initialSourceArticleId) {
      setValue('sourceArticleId', initialSourceArticleId);
      refetch(initialSourceArticleId);
    }
  }, [initialSourceArticleId, refetch, setValue]);

  useEffect(() => {
    if (!initialCustomerId) return;
    customersApi.getSlim(initialCustomerId).then((response) => {
      if (response.hasError) return;
      const option = { value: response.data.id, label: response.data.name };
      destinationWasSelected.current = true;
      setValue('destinationCustomerOption', option);
      setValue('destinationCustomerId', response.data.id);
      if (!initialSourceArticleId) {
        setValue('sourceCustomerOption', option);
        loadSourceCustomer(response.data.id);
      }
    });
  }, [initialCustomerId, initialSourceArticleId, loadSourceCustomer, setValue]);

  useEffect(() => {
    if (!source) return;
    setValue('sourceCustomerId', source.customerId);
    setValue('sourceArticleId', source.id);
    setValue('sourceCustomerOption', {
      value: source.customerId,
      label: source.customerName,
    });
    setValue('sourceArticleOption', {
      value: source.id,
      label: `${source.itemCode} · ${source.name}`,
    });
    if (!destinationWasSelected.current) {
      setValue('destinationCustomerId', source.customerId);
      setValue('destinationCustomerOption', {
        value: source.customerId,
        label: source.customerName,
      });
    }
    if (!destinationWasSelected.current) {
      setValue('destinationConceptId', source.conceptId);
    }
    const suggestedItemCode = `${source.itemCode}-COPY`;
    setValue(
      'itemCode',
      suggestedItemCode.length <= (source.itemCodeMaxLength ?? 100)
        ? suggestedItemCode
        : '',
    );
    setValue(
      'padding',
      Math.max(1, Math.min(2, source.itemCodeMaxLength ?? Number.MAX_VALUE)),
    );
    setValue('name', source.name);
    setValue(
      'branchIds',
      source.branches
        .filter((branch) => branch.isSource)
        .map((branch) => branch.id),
    );
    setValue(
      'variants',
      source.variants.map((variant) => ({
        variantId: variant.id,
        included: true,
        suppliers: true,
        purchaseInformation: !variant.isProductDevelopment,
        salesInformation: !variant.isProductDevelopment,
        artwork: !variant.isProductDevelopment,
        photo: !variant.isProductDevelopment,
        compositionGroups: variant.isProductDevelopment,
        inquiries:
          currentUser.betaFeatures &&
          variant.isProductDevelopment &&
          variant.suppliers.some((supplier) =>
            supplier.branches.some((branch) => branch.inquiryCount > 0),
          ),
        offers: currentUser.betaFeatures && variant.isProductDevelopment,
        supplierSelections: variant.isProductDevelopment
          ? variant.suppliers.map((supplier) => ({
              supplierId: supplier.id,
              inquiries:
                currentUser.betaFeatures &&
                supplier.branches.some((branch) => branch.inquiryCount > 0),
            }))
          : [],
        optionIds: variant.isProductDevelopment
          ? variant.options.map((option) => option.id)
          : [],
      })),
    );
    articlesApi
      .getDirectoryArticles(null, source.customerId)
      .then((response) => {
        if (!response.hasError) setValue('sourceArticles', response.data);
      });
  }, [source, setValue, currentUser.betaFeatures]);

  const loadSourceArticle = (option: ReactSelectOption) => {
    const nextArticleId = Number(option.value);
    setValue('sourceArticleOption', option);
    setValue('sourceArticleId', nextArticleId);
    refetch(nextArticleId);
  };

  const selectDestinationCustomer = (option: ReactSelectOption) => {
    destinationWasSelected.current = true;
    setValue('destinationCustomerOption', option);
    setValue('destinationCustomerId', Number(option.value));
    setValue('destinationConceptId', null);
  };

  const selectDestinationConcept = (conceptId: number | null) => {
    destinationWasSelected.current = true;
    setValue('destinationConceptId', conceptId);
  };

  useEffect(() => {
    if (!destinationCustomerId) return;
    customersApi.getConcepts(destinationCustomerId).then((response) => {
      if (!response.hasError) setValue('concepts', response.data);
    });
  }, [destinationCustomerId, setValue]);

  useEffect(
    () =>
      reactEvents.conceptCreated.sub(({ id, customerId }) => {
        if (!destinationCustomerId || customerId !== destinationCustomerId)
          return;
        destinationWasSelected.current = true;
        setValue('destinationConceptId', id);
        customersApi.getConcepts(destinationCustomerId).then((response) => {
          if (!response.hasError) setValue('concepts', response.data);
        });
      }),
    [destinationCustomerId, setValue],
  );

  useEffect(() => {
    reactEvents.routingChanged.dispatch({
      to: 'copyArticle',
      navObj: {
        sourceArticleId: sourceArticleId || null,
        customerId: destinationCustomerId || null,
        conceptId: destinationConceptId,
      },
      options: { notify: false, location: 'replace' },
    });
  }, [destinationConceptId, destinationCustomerId, sourceArticleId]);

  const targets = useMemo<ArticleCopyTarget[]>(
    () =>
      buildArticleNumbers(
        copyMode,
        itemCode,
        name,
        rangeStart,
        rangeEnd,
        padding,
        position,
      ),
    [copyMode, itemCode, name, padding, position, rangeEnd, rangeStart],
  );

  const itemCodeMinLength = source?.itemCodeMinLength ?? 0;
  const {
    maximumLength: itemCodeMaxLength,
    reservedDigits: rangeNumberLength,
    inputMaxLength: rangeFormatMaxLength,
  } = getArticleNumberLimits(
    copyMode,
    rangeStart,
    rangeEnd,
    padding,
    source?.itemCodeMaxLength,
  );
  const hasValidItemCodeLength = (value: string) =>
    value.length >= itemCodeMinLength && value.length <= itemCodeMaxLength;
  const itemCodeLengthInvalid = targets.some(
    (target) => !hasValidItemCodeLength(target.itemCode),
  );

  const descriptionInvalid = targets.some(
    (target) =>
      !target.name.trim() ||
      target.name.trim().length > articleDescriptionMaxLength,
  );

  const codes = useMemo(
    () => targets.map((target) => target.itemCode),
    [targets],
  );
  const availabilityResource = useArticleAvailability(codes);
  const availability = availabilityResource.data ?? {};
  const checking =
    availabilityResource.loading ||
    (!!targets.length &&
      !itemCodeLengthInvalid &&
      !availabilityResource.data &&
      !availabilityResource.error);

  const updateVariant = (
    variantId: number,
    change: Partial<VariantSelection>,
  ) => {
    setValue(
      'variants',
      getValues('variants').map((variant) =>
        variant.variantId === variantId ? { ...variant, ...change } : variant,
      ),
    );
  };

  const updateOptions = (variantId: number, optionIds: number[]) => {
    const sourceVariant = source?.variants.find(
      (variant) => variant.id === variantId,
    );
    if (!sourceVariant?.isProductDevelopment) return;
    const selectedOptionIds = new Set(optionIds);
    setValue(
      'variants',
      getValues('variants').map((selection) => {
        if (selection.variantId === variantId)
          return optionIds.length > 0
            ? {
                ...selection,
                included: true,
                compositionGroups: true,
                optionIds,
              }
            : {
                ...selection,
                included: true,
                compositionGroups: false,
                optionIds: [],
                suppliers: false,
                supplierSelections: [],
                inquiries: false,
                offers: false,
              };

        const linkedOption = sourceVariant.options.find(
          (option) => option.linkedVariantId === selection.variantId,
        );
        return linkedOption
          ? { ...selection, included: selectedOptionIds.has(linkedOption.id) }
          : selection;
      }),
    );
  };

  const onIgnoreClashesChange = useCallback(
    (value: boolean) => setValue('ignoreClashingItemCodes', value),
    [setValue],
  );
  const {
    firstArticleClashes,
    clashingItemCodeCount,
    suggestedRange,
    creationTargets: copyTargets,
    ignoredCount,
  } = useArticleClashes({
    targets,
    availability: availabilityResource,
    mode: copyMode,
    itemCode,
    start: rangeStart,
    end: rangeEnd,
    padding,
    position,
    minimumLength: itemCodeMinLength,
    maximumLength: itemCodeMaxLength,
    ignoreClashes: ignoreClashingItemCodes,
    onIgnoreClashesChange,
  });
  const unavailable = clashingItemCodeCount > 0;
  const applySuggestedRange = () => {
    if (
      !firstArticleClashes ||
      !suggestedRange.data ||
      suggestedRange.loading ||
      suggestedRange.error
    )
      return;
    setValue('rangeStart', suggestedRange.data.start);
    setValue('rangeEnd', suggestedRange.data.end);
  };

  const submit = async () => {
    if (!source || !canSubmit || copying) return;
    const response = await copy(source.id, {
      customerId: destinationCustomerId,
      conceptId: destinationConceptId,
      ignoreClashingItemCodes,
      branchIds,
      targets: copyTargets,
      article: { contact, tags, targetTiers },
      variants: variants
        .filter((variant) => variant.included)
        .map(({ included: _included, ...variant }) => {
          const sourceVariant = source.variants.find(
            (item) => item.id === variant.variantId,
          );
          if (!sourceVariant?.isProductDevelopment) return variant;
          const availableSupplierIds = new Set(
            sourceVariant.suppliers
              .filter((supplier) =>
                supplier.branches.some((branch) =>
                  branchIds.includes(branch.branchId),
                ),
              )
              .map((supplier) => supplier.id),
          );
          const supplierSelections = variant.supplierSelections
            .filter((supplier) => availableSupplierIds.has(supplier.supplierId))
            .map((supplier) => ({
              ...supplier,
              inquiries: currentUser.betaFeatures && supplier.inquiries,
            }));
          return {
            ...variant,
            suppliers: supplierSelections.length > 0,
            offers: currentUser.betaFeatures && variant.offers,
            inquiries: supplierSelections.some(
              (supplier) => supplier.inquiries,
            ),
            supplierSelections,
          };
        }),
    });
    if (response.hasError) return;
    toast({
      title: t('article.copy.queued', {
        count: response.data.targetCount,
      }),
      status: 'success',
      position: 'bottom-right',
    });
    reactEvents.routingChanged.dispatch(
      response.data.jobId
        ? { to: 'jobs.detail', navObj: { jobId: response.data.jobId } }
        : { to: 'jobs' },
    );
  };

  const hasSource = sourceArticleId === source?.id;
  const fieldWarnings = getCopyWarnings(
    {
      sourceCustomerId,
      hasSource,
      destinationCustomerId,
      branchCount: branchIds.length,
      variantCount: variants.filter((variant) => variant.included).length,
      itemCode,
      name,
      invalidLength: itemCodeLengthInvalid,
      minimumLength: itemCodeMinLength,
      maximumLength: itemCodeMaxLength,
      mode: copyMode,
      targetCount: targets.length,
      validationFailed: !!availabilityResource.error,
    },
    t,
    warningScope,
  );
  const warningItems: WarningItem[] = [
    ...getActiveWarnings(fieldWarnings, 'copy'),
    ...(!hasSource
      ? []
      : firstArticleClashes
      ? [
          {
            id: 'copy.firstArticleClash',
            targetId: fieldWarnings.itemCode.targetId,
            message: t(
              copyMode === 'range'
                ? 'article.create.firstArticleClash'
                : 'article.create.singleArticleClash',
              { itemCode: targets[0].itemCode },
            ),
            mandatory: true,
          },
        ]
      : copyTargets.flatMap<WarningItem>((target) => {
          const result = availability[target.itemCode];
          return result?.isValid === false
            ? [
                {
                  id: `copy.${target.itemCode}.availability`,
                  targetId: fieldWarnings.itemCode.targetId,
                  message: t('article.create.warningForArticle', {
                    itemCode: target.itemCode,
                    message:
                      result.errorMessage ?? t('article.copy.validationFailed'),
                  }),
                  mandatory: true,
                },
              ]
            : [];
        })),
  ];
  const canSubmit =
    hasSource &&
    destinationCustomerId > 0 &&
    branchIds.length > 0 &&
    copyTargets.length > 0 &&
    variants.some((variant) => variant.included) &&
    !firstArticleClashes &&
    !warningItems.some((warning) => warning.mandatory) &&
    copyTargets.every(
      (target) => availability[target.itemCode]?.isValid === true,
    ) &&
    !checking &&
    !availabilityResource.error &&
    !itemCodeLengthInvalid &&
    !descriptionInvalid &&
    (ignoreClashingItemCodes || !unavailable);

  const sourceArticleOptions = sourceArticles.map((article) => ({
    value: article.id,
    label: `${article.itemCode} · ${article.name}`,
  }));
  const conceptOptions = concepts.map((concept) => ({
    value: concept.id,
    label: concept.name,
  }));
  const selectedConceptOption =
    conceptOptions.find(
      (option) => Number(option.value) === destinationConceptId,
    ) ?? null;
  const includedVariants = variants.filter((variant) => variant.included);
  const includedArticleDataCount = [contact, tags, targetTiers].filter(
    Boolean,
  ).length;

  return {
    setValue,
    getValues,
    fieldWarnings,
    warningItems,
    firstArticleClashes,
    suggestedRange,
    applySuggestedRange,
    ignoredCount,
    availability,
    branchIds,
    canSubmit,
    checking,
    clashingItemCodeCount,
    conceptOptions,
    contact,
    copyError: copyError ?? availabilityResource.error,
    copyMode,
    copyTargets,
    copying,
    destinationConceptId,
    destinationCustomerId,
    destinationCustomerOption,
    destinationCustomerSearch,
    descriptionInvalid,
    error,
    hasValidItemCodeLength,
    ignoreClashingItemCodes,
    includedArticleDataCount,
    includedVariants,
    itemCode,
    itemCodeLengthInvalid,
    itemCodeMaxLength,
    itemCodeMinLength,
    loadSourceArticle,
    loadSourceCustomer,
    loading,
    name,
    padding,
    position,
    rangeEnd,
    rangeFormatMaxLength,
    rangeNumberLength,
    rangeStart,
    selectDestinationConcept,
    selectDestinationCustomer,
    selectedConceptOption,

    source,
    sourceArticleId,
    sourceArticleOption,
    sourceArticleOptions,
    sourceCustomerId,
    sourceCustomerOption,
    sourceCustomerSearch,
    submit,
    tags,
    targetTiers,
    targets,
    unavailable,
    updateOptions,
    updateVariant,
    variants,
  };
}

type CopyArticleContextValue = ReturnType<typeof useCopyArticleState>;
const CopyArticleContext = createContext<CopyArticleContextValue | null>(null);

export function CopyArticleProvider({
  children,
  ...props
}: CopyArticleProviderProps & { children: ReactNode }) {
  const value = useCopyArticleState(props);
  return (
    <CopyArticleContext.Provider value={value}>
      {children}
    </CopyArticleContext.Provider>
  );
}

export function useCopyArticle() {
  return useActiveContext(CopyArticleContext);
}
